import React, { useState, useEffect, useRef } from 'react';
import { Button } from "@/components/ui/button";
import { ArrowRight, X, ChevronLeft, ChevronRight, Images } from "lucide-react";
import { createPageUrl } from "@/utils";
import { useScrollReveal } from './useScrollReveal';

import { getProjectHref } from '@/components/plumbing/projectData';

const projects = [

  {
    image: "/images/optimized/0fcc0cc71_Modernbathroomunderconstructiondetails.webp",
    title: "Commercial Amenities Fitout",
    description: "Full amenities refurbishment including new copper and PVC pipework, sanitary fixture installation, and compliant waste connections."
  },
  {
    title: "Commercial Laundromat Fit-Out",
    description: "Complete commercial plumbing installation for a laundromat, including hot and cold water services, appliance connections and associated drainage.",
    images: [
      {
        image: "/images/optimized/performance/laundromat-fitout.webp",
        caption: "Completed commercial laundromat fit-out with plumbing services integrated throughout the facility."
      },
      {
        image: "https://media.base44.com/images/public/69a6a24481782401bea8b970/fb95176a9_IMG_8263.jpg",
        caption: "Commercial hot and cold water services installed and connected to multiple laundry appliances."
      }
    ]
  },
  {
    title: "Commercial Hot Water Installation",
    description: "Installation of a commercial hot water system, including associated pipework, valves, connections and drainage.",
    images: [
      {
        image: "/images/optimized/performance/hot-water-installation.webp",
        caption: "Commercial hot water system installation with dedicated water services and professionally installed copper pipework."
      }
    ]
  },
  {
    image: "/images/optimized/performance/civil-hydraulic-works.webp",
    title: "Civil Hydraulic Works",
    description: "Underground drainage installation including sewer, stormwater, and civil hydraulic connections for commercial development."
  },
  {
    title: "Commercial Kitchen Plumbing Fit-Out",
    description: "Commercial plumbing installation for a professional kitchen and wash-up area, including fixture connections, water services and drainage.",
    images: [
      {
        image: "/images/optimized/performance/commercial-kitchen-fitout.webp",
        caption: "Commercial wash-up area plumbing installation, including multiple stainless-steel sinks, appliance connections and drainage."
      }
    ]
  },
  {
    image: "/images/optimized/37441f7d7_F8ACA96E-3454-425F-AF87-0E7C74C811D8.webp",
    title: "Beauty Salon Fit-Out",
    description: "Hydraulic rough-in and final fit-off for a commercial beauty salon, including basin waste, hot and cold water services."
  },
];

function ProjectModal({ project, onClose }) {
  const images = project.images || [{ image: project.image }];
  const [currentIdx, setCurrentIdx] = useState(0);
  const isMulti = images.length > 1;
  const touchStartX = useRef(null);

  useEffect(() => {
    if (!isMulti) return;
    const handleKey = (e) => {
      if (e.key === 'ArrowLeft') setCurrentIdx(i => (i - 1 + images.length) % images.length);
      if (e.key === 'ArrowRight') setCurrentIdx(i => (i + 1) % images.length);
      if (e.key === 'Escape') onClose();
    };
    window.addEventListener('keydown', handleKey);
    return () => window.removeEventListener('keydown', handleKey);
  }, [isMulti, images.length, onClose]);

  const goPrev = () => setCurrentIdx(i => (i - 1 + images.length) % images.length);
  const goNext = () => setCurrentIdx(i => (i + 1) % images.length);

  const handleTouchStart = (e) => { touchStartX.current = e.touches[0].clientX; };
  const handleTouchEnd = (e) => {
    if (touchStartX.current === null) return;
    const diff = touchStartX.current - e.changedTouches[0].clientX;
    if (Math.abs(diff) > 50) {
      if (diff > 0) goNext(); else goPrev();
    }
    touchStartX.current = null;
  };

  const img = images[currentIdx];

  return (
    <div
      className="fixed inset-0 z-50 bg-black/90 flex items-center justify-center p-6 animate-in fade-in duration-200"
      onClick={onClose}
    >
      <div
        className="relative bg-[#0d0d0d] border border-white/10 max-w-3xl w-full max-h-[90vh] overflow-y-auto"
        onClick={e => e.stopPropagation()}
      >
        {/* Close button */}
        <button
          onClick={onClose}
          className="absolute top-4 right-4 z-20 text-gray-400 hover:text-white transition-colors bg-[#0d0d0d]/80 p-1.5"
        >
          <X className="w-5 h-5" />
        </button>

        {/* Image */}
        <div className="relative" onTouchStart={isMulti ? handleTouchStart : undefined} onTouchEnd={isMulti ? handleTouchEnd : undefined}>
          <img
            src={img.image}
            alt={project.title}
            className="w-full object-cover max-h-[55vh]"
          />
          <div className="absolute inset-0 flex items-center justify-center pointer-events-none" style={{ transform: 'rotate(-30deg)' }}>
            <span className="text-white font-bold text-sm tracking-widest uppercase select-none" style={{ opacity: 0.22, textShadow: '0 1px 3px rgba(0,0,0,0.5)', letterSpacing: '0.15em' }}>Oliver &amp; Sons Plumbing</span>
          </div>
          {isMulti && (
            <>
              <button
                onClick={goPrev}
                className="absolute left-3 top-1/2 -translate-y-1/2 z-10 bg-black/60 hover:bg-black/80 text-white p-2 transition-colors"
                aria-label="Previous image"
              >
                <ChevronLeft className="w-6 h-6" />
              </button>
              <button
                onClick={goNext}
                className="absolute right-3 top-1/2 -translate-y-1/2 z-10 bg-black/60 hover:bg-black/80 text-white p-2 transition-colors"
                aria-label="Next image"
              >
                <ChevronRight className="w-6 h-6" />
              </button>
              <div className="absolute bottom-3 left-1/2 -translate-x-1/2 z-10 bg-black/60 px-3 py-1 text-white text-xs font-medium tracking-wide">
                {currentIdx + 1} / {images.length}
              </div>
            </>
          )}
        </div>

        {img.caption && (
          <p className="px-8 py-4 text-gray-400 text-xs leading-relaxed border-b border-white/5">{img.caption}</p>
        )}

        {/* Content */}
        <div className="p-8">
          <h3 className="text-white font-bold text-xl mb-4">{project.title}</h3>
          <div className="h-px bg-white/10 mb-4" />
          <p className="text-gray-400 text-sm leading-relaxed">{project.description}</p>
        </div>
      </div>
    </div>
  );
}

export default function ProjectsSection() {
  const [selected, setSelected] = useState(null);
  const ref = useScrollReveal();

  return (
    <section id="projects" className="pt-28 md:pt-36 pb-12 md:pb-16 bg-[#080808]">
      <div className="section-divider" />
      <div ref={ref} className="reveal max-w-7xl mx-auto px-5 sm:px-6">
        {/* Header */}
        <div className="max-w-2xl mb-4">
          <p className="text-[#60a5fa] font-semibold tracking-[0.2em] uppercase text-xs mb-5">Portfolio</p>
          <h2 className="font-bold mb-4" style={{ fontSize: 'clamp(1.6rem, 5vw, 3rem)' }}>
            <span className="bg-gradient-to-r from-[#d0d0d0] via-[#ffffff] to-[#d0d0d0] bg-clip-text text-transparent">
              Project Portfolio
            </span>
          </h2>
          <p className="text-gray-400 text-lg mb-3">
            A selection of commercial and shopfit plumbing works delivered across South East Queensland.
          </p>
        </div>

        {/* Subtle label */}
        <p className="text-gray-500 text-xs tracking-widest uppercase mb-12 border-t border-white/5 pt-6">
          Commercial Hydraulic & Shopfit Installations
        </p>

        {/* Grid */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5 mb-10">
          {projects.map((project, index) => (
            <div
              key={index}
              className="group border border-white/5 hover:border-[#2563eb]/30 transition-all duration-300 overflow-hidden cursor-pointer card-hover-glow"
              onClick={() => setSelected(project)}
            >
              <div className="overflow-hidden aspect-[4/3] relative">
                <img
                  src={project.images ? project.images[0].image : project.image}
                  alt={project.title}
                  className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-[1.03]"
                  loading="lazy"
                  decoding="async"
                  width="800"
                  height="600"
                />
                <div className="absolute inset-0 bg-black/20 group-hover:bg-black/35 transition-colors duration-300" />
                <div className="absolute inset-0 flex items-center justify-center pointer-events-none" style={{ transform: 'rotate(-30deg)' }}>
                  <span className="text-white font-bold text-sm tracking-widest uppercase select-none" style={{ opacity: 0.22, textShadow: '0 1px 3px rgba(0,0,0,0.5)', letterSpacing: '0.15em' }}>Oliver &amp; Sons Plumbing</span>
                </div>
                {project.images && project.images.length > 1 && (
                  <>
                    <div className="absolute top-3 right-3 z-10 flex items-center gap-1.5 bg-black/70 backdrop-blur-sm px-2.5 py-1.5 text-white text-xs font-medium tracking-wide">
                      <Images className="w-3.5 h-3.5" />
                      <span>{project.images.length} Photos</span>
                    </div>
                    <div className="absolute inset-0 flex items-end justify-center pb-6 pointer-events-none opacity-70 md:opacity-0 md:group-hover:opacity-100 transition-opacity duration-300">
                      <span className="flex items-center gap-2 text-white font-semibold text-sm tracking-wide bg-black/60 backdrop-blur-sm px-4 py-2">
                        View Project <ArrowRight className="w-4 h-4" />
                      </span>
                    </div>
                  </>
                )}
              </div>
              <div className="p-6 bg-[#0d0d0d]">
                <h3 className="text-white group-hover:text-gray-100 font-bold text-sm mb-2 project-title-underline">{project.title}</h3>
                {project.images && project.images.length > 1 && (
                  <div className="flex items-center gap-1.5 text-[#60a5fa] text-[0.65rem] font-medium tracking-[0.15em] uppercase mb-2">
                    <Images className="w-3 h-3" />
                    <span>{project.images.length} Photos</span>
                  </div>
                )}
                <p className="text-gray-400 text-xs leading-relaxed">{project.description}</p>
                {getProjectHref(project.title) && (
                  <a href={getProjectHref(project.title)} onClick={e => e.stopPropagation()} className="mt-4 inline-flex items-center gap-2 text-[#60a5fa] text-sm font-semibold hover:text-white">View scope &amp; project details <ArrowRight className="w-4 h-4" /></a>
                )}
              </div>
            </div>
          ))}
        </div>

        {/* View All Button */}
        <div className="text-center">
          <a href={createPageUrl("Projects")}>
            <Button className="bg-transparent border border-white/20 hover:border-[#2563eb]/50 hover:bg-[#1e3a5f]/20 text-gray-300 hover:text-white px-10 py-5 font-semibold transition-all duration-300 rounded-sm group">
              View All Projects
              <ArrowRight className="w-4 h-4 ml-2 group-hover:translate-x-1 transition-transform duration-300" />
            </Button>
          </a>
        </div>
      </div>

      {/* Modal */}
      {selected && <ProjectModal project={selected} onClose={() => setSelected(null)} />}
    </section>
  );
}
